import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseService, BreadcrumbService, QueryComponent, RouteFns } from '@core';
import { Account } from '@models';
import { UtilFns } from '@utils';
import { MenuItem } from 'primeng/api';
import { AccountDbQueryService } from './services/account-db-query.service';

/**
 * Training Mode landing page. Shown to PrincipalAccountAdmins whose AccountAdmin row is locked
 * (TrainingUnlockedUntil is null or in the past). Renders the same submenu structure as
 * AccountManagerComponent but every item is inert with a lock icon, and clicking any item
 * shows a "contact your supplier" dialog.
 */
@Component({
  selector: 'prox-account-manager-locked',
  templateUrl: './account-manager-locked.component.html',
  standalone: false,
})
export class AccountManagerLockedComponent extends QueryComponent {
  items: MenuItem[] = [];
  accountId?: string;
  accountName?: string;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbQueryService);
  }

  async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');

    const result = await this.dbQueryService.fetchEntityByKey(Account, this.accountId, true);
    this.accountName = (result.entity as Account)?.name;

    this.breadcrumbService.setItems([{ label: this.accountName ?? '' }]);
    this.setTitle('Account Locked for Training');

    this.items = this.buildLockedItems();
    // Every leaf here has its routerLink replaced by a locked-dialog command, so nothing can
    // match the URL and all sections stay collapsed. Called anyway so this menu does not drift
    // from its three siblings if that ever changes.
    RouteFns.expandActiveSection(this.items, this.router.url);
    this.isPageReady = true;
  }

  private buildLockedItems(): MenuItem[] {
    // Mirrors the menu structure in account-manager.component.ts. Every leaf has its
    // routerLink replaced with a command that shows the locked dialog.
    const lockCmd = () => this.showLockedMessage();
    const locked = (label: string, icon: string): MenuItem => ({
      label,
      icon,
      command: lockCmd,
    });

    return [
      {
        label: '<span class="text-l font-bold">Administration</span>',
        escape: false,
        expanded: false,
        items: [
          locked('Information', 'pi pi-fw pi-cog'),
          locked('Administrators', 'fa-duotone fa-solid fa-user-tie-hair-long'),
          locked('Administrator Groups', 'fa-sharp-duotone fa-solid fa-people-group'),
          locked('Roles', 'fa-sharp-duotone fa-solid fa-key'),
          locked('Notification Submission Status', 'pi pi-fw pi-cog'),
        ],
      },
      {
        label: '<span class="text-l font-bold">Organization</span>',
        escape: false,
        expanded: false,
        items: [
          locked('Account Users', 'fa-duotone fa-light fa-users'),
          locked('Budget Product Tags', 'fa-regular fa-tags'),
          locked('Budgets', 'fa-sharp fa-regular fa-piggy-bank'),
          locked('Shipping User Groups', 'pi pi-fw pi-truck'),
          locked('Shipping Addresses', 'fa-sharp fa-solid fa-address-book'),
          locked('Billing Addresses', 'fa-sharp fa-regular fa-file-invoice-dollar'),
          locked('Product Catalog', 'ffa-sharp fa-regular fa-book-open'),
        ],
      },
      {
        label: '<span class="text-l font-bold">Communication</span>',
        escape: false,
        expanded: false,
        items: [
          locked('Notification Templates', 'fa-sharp-duotone fa-regular fa-copy'),
          locked('Notification Settings', 'fa-sharp fa-regular fa-gear'),
        ],
      },
      {
        label: '<span class="text-l font-bold">Program Preparation</span>',
        escape: false,
        expanded: false,
        items: [
          locked('Product Tags', 'fa-sharp fa-regular fa-tags'),
          locked('Kit Tags', 'fa-duotone fa-regular fa-suitcase'),
          locked('Program User Groups', 'fa-duotone fa-solid fa-user-group'),
          locked('Purchase Orders', 'fa-solid fa-money-check-dollar-pen'),
          locked('Procurement Cards', 'pi pi-fw pi-credit-card'),
          locked('Proximity Organization', 'fa-regular fa-sitemap'),
        ],
      },
      {
        label: '<span class="text-l font-bold">Programs</span>',
        escape: false,
        icon: 'fa-sharp-duotone fa-regular fa-circle-nodes',
        command: lockCmd,
      },
    ];
  }

  showLockedMessage() {
    this.dialogService.okDialog(
      'Locked for Training',
      'This account is locked for training. Contact your supplier to unlock.',
    );
  }
}
